import React, { useEffect, useState } from 'react';
import { Header, Tab } from 'semantic-ui-react'
import Loader from '../../components/loader';
import AuthService from '../../service/auth';
import { ROLES } from '../../constnats/user';
import { AuthContext } from '../../App';
import RouteC from '../../constnats/routeConstants';
import CompaniesTable from '../../components/admin/company/companiesTable';
import { UpdatesTable } from '../../components/admin/updates/updatesTab';
import CompanyRepo from '../../utils/repository/companyRepo';
import { SelectField } from '../../components/form/fields';
import UserRepo from '../../utils/repository/userRepo';
import KeyValue from '../../structures/keyValePair';
import CollectionUtils from '../../utils/collections';
import useTasks from '../../hooks/task/useTasks';
import TasksTable from '../../components/admin/task/tasksTable';
import { getPaneCfg } from '../../utils/general';
import { TASK_STATUSES_CONSIDERED_ACTIVE } from '../../constnats/task';
import { ReviewType } from '../../components/financialTables/configuration/constants';
import { REVIEW_STATUS } from '../../constnats/review';
import { SORT_BY } from '../../constnats/tableUpdate';

function useEmployeesKV(includeDisabled = false) {

    const [users, setUsers] = useState([])

    useEffect(() => {
        UserRepo.list({ userRoles: [ROLES.EMPLOYEE], includeDisabled },
            users => setUsers(CollectionUtils.sortArrByString(users, u => u.fullName)
                .map(t => new KeyValue(t.id, t.fullName))))
    }, [includeDisabled])

    return users
}

function EmployeeHome() {
    const [userId, setUserId] = useState(AuthService.getUserId())
    const users = useEmployeesKV()

    const viewAsUserName = users.find(u => u.key === userId)?.value ?? ''
    const ownerOptions = [{ id: userId, fullName: viewAsUserName }]

    const panes = []

    panes.push(getPaneCfg("My Updates",
        <UpdatesTable
            query={{ notPhase: "APPLIED", owner: userId }}
            clientFilter={userUpdatesFilter()}
            ownerOptions={ownerOptions}
            detailed={false}
        />))
    panes.push(getPaneCfg("Updates For Peer Review",
        <UpdatesTable
            query={{ phases: ["READY_FOR_PEER_REVIEW", "READY_FOR_QA_REVIEW"], assignee: userId }}
            clientFilter={pendingPeerFilter(userId)}
            ownerOptions={ownerOptions}
            detailed={false}
        />))

    if (AuthService.hasAnyGlobalRole([ROLES.QA])) {
        panes.push(getPaneCfg("Updates For QA Review",
            AuthService.getUserId() === userId ? <UpdatesTable
                key="updatesForQAReview"
                initialSort={SORT_BY.UPDATED_ON}
                query={{ phases: ["READY_FOR_QA_REVIEW"] }}
                clientFilter={pendingQAFilter(userId)}
                ownerOptions={ownerOptions}
                detailed={false}
            /> : "Not supported"))
    }
    panes.push(getPaneCfg("My Companies",
        <Companies owner={userId} />))
    panes.push(getPaneCfg("My Tasks", <TasksTable filters={{assignedUserId:userId,
        statuses:TASK_STATUSES_CONSIDERED_ACTIVE}}/>))

    panes.push(getPaneCfg("Contributor to Companies",
        <Companies contributor={userId} />))

    return (
        <>
            <SelectField
                className="smallField"
                label="View As: "
                value={userId}
                onChange={setUserId}
                valuesMap={users} number={true}
            />
            <br />
            <Tab panes={panes} />
        </>
    )
}

class Home extends React.Component {

    componentWillUnmount() {
        Cache.clean()
    }

    render() {
        return <div>
            {AuthService.hasRole(this.context, ROLES.EMPLOYEE) && <EmployeeHome />}
        </div>
    }
}

function pendingQAFilter(userId) {
    return u => {
        const reviews = u.reviews.filter(r => r.status !== REVIEW_STATUS.DISCONTINUED)
        const qaReviews = reviews.filter(r => r.type === "VISUAL_CHECK")
        const nonQaReviews = reviews.filter(r => r.type !== "VISUAL_CHECK")
        return nonQaReviews.every(r =>
            (r.status === "APPROVED" && r.ownerId !== userId)) &&
            (
                (qaReviews.length === 0) ||
                qaReviews.find(r =>
                    (r.status === "PENDING_REVISION" ||
                        r.status === "IN_PROGRESS")
                    && r.ownerId === userId)
            )
    }
}


function pendingPeerFilter(userId) {
    return (u) => {
        if (!u.minFullChecks && !u.minCDChecks) {
            return false;
        }
        const reviews = u.reviews.filter(r => r.status !== REVIEW_STATUS.DISCONTINUED)
        const peerReviews = reviews.filter((r) => r.type !== ReviewType.VISUAL_CHECK);
        const userReview = peerReviews.find((r) => r.ownerId === userId);
        const userReviewResolved = userReview?.status === 'APPROVED' || userReview?.status === 'REJECTED';
        
        if (userReviewResolved) {
            return false;
        }

        const fullChecksCount = peerReviews.filter((r) => r.type === ReviewType.FULL_CHECK).length;
        const cdChecksCount = peerReviews.filter((r) => r.type === ReviewType.CD_CHECK).length;
        const countsSatisfied = fullChecksCount >= u.minFullChecks && cdChecksCount >= u.minCDChecks;
        
        // Pending if counts are not satisfied and the user has no review yet,
        // or if counts are satisfied but the user's review is still unresolved
        return !countsSatisfied || !!userReview;
    };
}

function userUpdatesFilter() {
    return u => {
        const reviews = u.reviews
        return reviews.length === 0 ||
            reviews.find(r => r.status !== "APPROVED") ||
            reviews.every(r => r.status === "APPROVED")
    }
}

function Companies({ owner, contributor }) {
    const [companies, setCompanies] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        setLoading(true)
        const params = {}
        if (owner) {
            params.owner = owner

        }
        if (contributor) {
            params.contributor = contributor
        }
        new Promise(resolve => {
            CompanyRepo.listInternal(params, resolve)
        }).then(companies => {
            setCompanies(companies)
            setLoading(false)
        })
    }, [owner, contributor])

    return loading ? <Loader /> : <>
        <Header as="h5" textAlign='center' className="subPageHeader">PUBLIC</Header>
        <CompaniesTable companies={companies.filter(c => c.visibility === "PUBLIC")}
            companyUrlPrefix={RouteC.EDIT_COMPANY_CONFIG_INTERNAL} />
        <Header as="h5" textAlign='center' className="subPageHeader">INTERNAL</Header>
        <CompaniesTable companies={companies.filter(c => c.visibility === "INTERNAL")}
            companyUrlPrefix={RouteC.EDIT_COMPANY_CONFIG_INTERNAL} />
    </>
}


class Cache {

    static _cacheMap = {};
    static set(key, val) {
        Cache._cacheMap[key] = val
    }

    static get(key, valuePovider) {
        let res = Cache._cacheMap[key]
        if (res === undefined) {
            res = valuePovider()
            Cache.set(key, res)
        }
        return res
    }

    static remove(key) {
        Cache._cacheMap[key] = undefined
    }

    static clean() {
        Cache._cacheMap = {}
    }
}

Home.contextType = AuthContext;

export default Home
